import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout';
import { Component, inject, Inject } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { AppState } from '@app/modules/core/store/app.state';
import { LoggedInModuleType } from '@app/modules/shared/interfaces/logged-in-module.type';
import { LoginResponse } from '@app/modules/shared/interfaces/login-response.type';
import { AuthService } from '@app/services/auth.service';
import { Store } from '@ngrx/store';
import { Observable, map, shareReplay } from 'rxjs';
import { environment as env } from 'environments/environment';
import * as AuthActions from '../../modules/core/store/actions/auth.actions';
import * as fromAuth from '../../modules/core/store/reducers/auth.reducers';
import { HelperService } from '@app/services/helper.service';
import { LoggingService, LogLevel } from '@app/services/logging.service';
import { MatDialogRef } from '@angular/material/dialog';
import { ErrorShowDialogComponent } from '@app/modules/shared/components/error-show-dialog/error-show-dialog.component';

@Component({
    selector: 'app-line-auth',
    templateUrl: './line-auth.component.html',
    styleUrl: './line-auth.component.scss',
    standalone: false
})
export class LineAuthComponent {
  static readonly COMPONENT_NAME = 'LineAuthComponent';
  loggingService = inject(LoggingService);

  errorMessage = '';
  loaded: any;
  s3Url = '';
  isMobile: boolean = false;
  loginResponse!: LoginResponse;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.TabletPortrait])
    .pipe(
      map(result => {
        console.log("== result ==", result);
        return result.matches;
      }),
      shareReplay()
    );

  constructor(
    private breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute, private router: Router,
    private store: Store<AppState>,
    private helper: HelperService,
    private authService: AuthService, private snackBar: MatSnackBar) {
    this.s3Url = env?.bucketUrl;
  }

  ngOnInit(): void {
    this.isHandset$.subscribe(r => {
      console.log('IS HANDSET:', r);
      this.isMobile = r;
    });

    this.store.select('auth').subscribe((authState: fromAuth.State) => {
      console.log('Current AuthState:', authState);
      if (authState?.accessToken && authState?.refreshToken && authState?.user && authState?.loggedInModule) {
        this.loginResponse = { accessToken: authState.accessToken, refreshToken: authState.refreshToken, user: authState.user, roles: [authState.loggedInModule] };
      }
    });

    this.route.queryParams.subscribe((qp) => {
      const ORIGIN_URL = window.location.origin;

      if (qp['error'] || qp['error_description']) {
        this.errorMessage = qp['error'];
      } else {

        // Now send this auth code to backend for completing line authorization ....
        const REDIRECT_URI = ORIGIN_URL + '/line-auth-callback';


        if (qp['state'].includes('-ct-connect')) {

          this.authService.authorizeLineAccount({ ...qp, redirectUrl: REDIRECT_URI }).subscribe(r => {
            console.log('Line Authorize Server Response:', r);

            if (r.data.isAuthorizedSuccessfully) {
              this.snackBar.open('LINE login success! Redirecting...', 'Success', {
                duration: 4000
              });

              // Dispatch the update user in the store...
              this.loginResponse.user = { ...this.loginResponse.user, ...r.data.user };
              console.log('Updated Login Object:', this.loginResponse);

              console.log('loginResponse:', this.loginResponse);
              if (this.loginResponse.user.isGrower && (this.loginResponse.user.isOrgUser || this.loginResponse.user.isOrgAdmin)) {
                this.store.dispatch(AuthActions.loginAreaChooseDialog(this.loginResponse));
              } else {
                let loggedInModule: LoggedInModuleType;
                loggedInModule = this.loginResponse.roles[0];    // When there is only one role, just choose that....
                this.store.dispatch(AuthActions.loginSuccess({ ...this.loginResponse, loggedInModule }));
              }

            } else {
              console.log('Authorization Error:', r.data.error);
              this.errorMessage = 'Authorization Error';
              if (r.data.error) {
                this.errorMessage += ': ' + r.data.error;
              }
            }

          }, err => {
            this.helper.showHttpErrorMsg(err, 5500);
          });
        }

        if (qp['state'].includes('-ct-login')) {

          this.authService.loginWithLine({ ...qp, redirectUrl: REDIRECT_URI }).subscribe((loginResponse: LoginResponse) => {
            this.loggingService.log(LogLevel.DEBUG, LineAuthComponent.COMPONENT_NAME, 'Line Login Response:', loginResponse);

            if (loginResponse.user.isGrower && (loginResponse.user.isOrgUser || loginResponse.user.isOrgAdmin)) {
              this.loggingService.log(LogLevel.DEBUG, LineAuthComponent.COMPONENT_NAME, 'Prompting user to choose login area');
              this.store.dispatch(AuthActions.loginAreaChooseDialog({
                accessToken: loginResponse.accessToken,
                refreshToken: loginResponse.refreshToken,
                user: loginResponse.user,
                roles: loginResponse.roles
              }));
            } else {
              let loggedInModule: LoggedInModuleType;
              loggedInModule = loginResponse.roles[0];    // When there is only one role, just choose that....
              this.loggingService.log(LogLevel.DEBUG, LineAuthComponent.COMPONENT_NAME, 'Dispatching login success for module:', loggedInModule);
              this.store.dispatch(AuthActions.loginSuccess({
                accessToken: loginResponse.accessToken,
                refreshToken: loginResponse.refreshToken,
                user: loginResponse.user,
                loggedInModule: loggedInModule
              }));
            }
          }, err => {
            let ref: MatDialogRef<ErrorShowDialogComponent> | MatSnackBarRef<TextOnlySnackBar> = this.helper.showHttpErrorMsg(err, -1);
            const isMatDialogRef = (ref: any): ref is MatDialogRef<ErrorShowDialogComponent> =>
              ref.close !== undefined && ref.afterClosed !== undefined;

            const isMatSnackBarRef = (ref: any): ref is MatSnackBarRef<TextOnlySnackBar> =>
              ref.dismiss !== undefined && ref.afterDismissed !== undefined;

            if (isMatDialogRef(ref)) {
              ref.afterClosed().subscribe(() => {
                this.loggingService.log(LogLevel.DEBUG, LineAuthComponent.COMPONENT_NAME, 'Dialog was closed');
                this.router.navigateByUrl('/login');
              });
            } else if (isMatSnackBarRef(ref)) {

              ref.afterDismissed().subscribe(() => {
                this.loggingService.log(LogLevel.DEBUG, LineAuthComponent.COMPONENT_NAME, 'Snackbar was dismissed');
                this.router.navigateByUrl('/login');
              });
            } else {
              console.error('Unknown ref type');
              this.router.navigateByUrl('/login');
            }
          });
        }

      }
    });
  }

}
